/**
 * Frontend types mirroring the import-capability discovery + options API.
 *
 * Backend contract:
 *   good-outcomes-api/app/api/models/import_capabilities.py
 *   good-outcomes-api/app/api/routes/import_capabilities.py
 */

export type CapabilityType =
  | 'source.import'
  | 'destination.collection'
  | 'root.monitor_setup';

export type CredentialMode = 'organization' | 'creator_user' | 'effective';

export type EffectiveSecretSource =
  | 'session'
  | 'user'
  | 'organization'
  | 'environment'
  | 'unset';

export interface SecretStatus {
  key: string;
  label: string;
  description?: string;
  required: boolean;
  sensitive: boolean;
  scopes: string[];
  user_value_set: boolean;
  organization_value_set: boolean;
  environment_value_set: boolean;
  effective_source: EffectiveSecretSource;
}

export interface CapabilityField {
  id: string;
  type: string;
  label?: string;
  description?: string;
  /**
   * Inline hint text. Declared as `help:` in a SKILL.md field; carried here
   * so a primitive can render it. Note most primitives currently render
   * `description` only — a number field reads either.
   */
  help?: string;
  required?: boolean;
  default?: unknown;
  /** Bounds for `number` fields. The backend re-clamps authoritatively. */
  min?: number;
  max?: number;
  step?: number;
  options?: Array<{ value: string; label: string }>;
  options_provider?: {
    script?: string;
    args?: string[];
    backend_tool?: string;
    /**
     * Other field ids on the same capability whose values this resolver
     * actually consumes (e.g. `brand_id` depends on `account_code`).
     * Used by `useCapabilityOptions` to scope the React Query cache key
     * to ONLY these context entries — without it, every keystroke on an
     * unrelated field (`extra_filter`, etc.) would invalidate the cache
     * and re-fire every async resolver in the step.
     * When absent, the hook falls back to keying on the whole context
     * (the pre-existing behaviour); declare `depends_on` to opt-in to
     * the scoped key.
     */
    depends_on?: string[];
  };
  required_when?: Record<string, unknown>;
}

export interface CapabilitySchema {
  fields: CapabilityField[];
}

export interface ImportCapability {
  skill_name: string;
  capability_id: string;
  type: CapabilityType | string;
  label: string;
  description?: string;
  requires_secrets: string[];
  source_record_type?: string | null;
  schedule_constraints?: Record<string, unknown> | null;
  schedule?: Record<string, unknown> | null;
  credential_modes?: Record<string, unknown> | null;
  ui_flow?: Record<string, unknown> | null;
  implementation?: Record<string, unknown> | null;
  config_schema?: CapabilitySchema | null;
  cursor_schema?: CapabilitySchema | null;
  output_schema?: CapabilitySchema | null;
}

export interface ImportCapabilitySkill {
  skill_name: string;
  name: string;
  description: string;
  version: string;
  capabilities: ImportCapability[];
  secret_statuses: SecretStatus[];
}

export interface ImportCapabilityOption {
  value: string;
  label: string;
}

export interface ImportCapabilityOptionsResponse {
  field_id: string;
  options: ImportCapabilityOption[];
}

/**
 * POST /v1/admin/import-jobs payload shape — mirrors backend
 * ImportJobCreateRequest in good-outcomes-api/app/schemas/import_jobs.py.
 */
export interface ImportJobCreateRequest {
  name: string;
  source_skill_name: string;
  source_capability_id: string;
  source_config: Record<string, unknown>;
  destination_skill_name: string;
  destination_capability_id: string;
  destination_config: Record<string, unknown>;
  schedule_config: Record<string, unknown>;
  credential_mode?: 'organization' | 'creator_user' | 'effective';
  config_version?: number;
}

/** Minimal response shape — just the fields the wizard uses post-Save. */
export interface ImportJobResponse {
  id: string;
  organization_id: string;
  name: string;
  status: string;
  destination_config: Record<string, unknown> | null;
}

/**
 * Derived readiness state — scoped to the secrets a specific capability
 * needs (capability.requires_secrets). A skill can advertise multiple
 * capabilities with overlapping but distinct secret requirements; readiness
 * for capability A should not be coloured by an unrelated secret declared
 * for capability B.
 *
 * Drives the source-picker badge and the "Connect …" CTA.
 */
export type SkillReadiness = 'ready' | 'needs_setup' | 'partial';

export const computeCapabilityReadiness = (
  requiredKeys: string[],
  statuses: SecretStatus[],
): SkillReadiness => {
  if (requiredKeys.length === 0) {
    return 'ready';
  }
  const relevant = statuses.filter((s) => requiredKeys.includes(s.key));
  const allSet = relevant.every((s) => s.effective_source !== 'unset');
  if (allSet && relevant.length === requiredKeys.length) {
    return 'ready';
  }
  const anySet = relevant.some((s) => s.effective_source !== 'unset');
  return anySet ? 'partial' : 'needs_setup';
};

/**
 * @deprecated Use {@link computeCapabilityReadiness} with the selected
 * capability's `requires_secrets`. Kept as a thin wrapper for callers that
 * have only a SecretStatus list to hand (e.g. the readiness helper test).
 */
export const computeSkillReadiness = (
  statuses: SecretStatus[],
): SkillReadiness => {
  const requiredKeys = statuses.filter((s) => s.required).map((s) => s.key);
  return computeCapabilityReadiness(requiredKeys, statuses);
};
